import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  MenuItem,
  USER_DASHBOARD_MENU,
  ADMIN_DASHBOARD_MENU,
  IB_DASHBOARD_MENU,
} from '../../constants/menuConstants';
import { useAuth } from '../../contexts/AuthContext';
import SidebarToggle, { SidebarMode } from './SidebarToggle';
import MobileDrawer from './MobileDrawer';

interface SidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
  logo?: string;
}

const Sidebar = ({ isOpen = true, onToggle, logo }: SidebarProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('user');

  const navigation =
    user?.role === 'super_admin' ? ADMIN_DASHBOARD_MENU : USER_DASHBOARD_MENU;

  const filteredNavigation = (() => {
    if (user?.role === 'super_admin') {
      return navigation;
    }
    // if (!user?.referralCode) {
    //   return navigation.filter(item => item.name !== 'IB Accounts');
    // }
    if (sidebarMode === 'user') {
      return navigation;
    }
    return IB_DASHBOARD_MENU;
  })();

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(item => item !== itemName)
        : [...prev, itemName]
    );
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);
    const isActive = location.pathname === item.href;

    return (
      <li key={item.name}>
        <div
          className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all cursor-pointer ${
            isActive
              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
          }`}
          style={{ paddingLeft: `${level * 16 + 16}px` }}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.name);
            }
          }}
        >
          <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
          <Link
            to={item.href}
            className="flex-1 text-sm font-medium"
            onClick={e => {
              if (hasChildren) {
                e.preventDefault();
              }

              if (window.innerWidth < 1024 && onToggle) {
                onToggle();
              }
            }}
          >
            {item.name}
          </Link>
          {hasChildren && (
            <ChevronRight
              className={`mr-2 h-4 w-4 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
            />
          )}
        </div>
        {hasChildren && isExpanded && item.children && (
          <ul className="mt-2 mb-2 space-y-1">
            {item.children.map((child: MenuItem) =>
              renderMenuItem(child, level + 1)
            )}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isOpen}
        onToggle={onToggle!}
        logo={logo || '/Forex-crm-logo.png'}
        isAdmin={user?.role === 'super_admin'}
      >
        {user?.referralCode && user?.role !== 'super_admin' && (
          <SidebarToggle mode={sidebarMode} onModeChange={setSidebarMode} />
        )}

        <nav className="flex-1 px-3 pt-3 overflow-y-auto">
          <ul className="space-y-2 pb-4">
            {filteredNavigation.map(item => renderMenuItem(item))}
          </ul>
        </nav>
      </MobileDrawer>

      {/* Desktop Sidebar - Original behavior */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:bg-white lg:border-r lg:border-gray-200 lg:h-full">
        <div className="flex flex-col h-full">
          {user?.referralCode && user?.role !== 'super_admin' && (
            <SidebarToggle mode={sidebarMode} onModeChange={setSidebarMode} />
          )}

          <nav className="flex-1 px-3 pt-4  overflow-y-auto">
            <ul className="space-y-2 pb-4">
              {filteredNavigation.map(item => renderMenuItem(item))}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
